import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from '../AuthContext';
import { encryptId } from "../utils/crypto";
import { motion, AnimatePresence } from "framer-motion";

export default function NotificationPopup({ isOpen, onClose, userId }) {
  const [notifications, setNotifications] = useState([]);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (isOpen && userId) {
      fetch(`http://localhost:5000/notifications/${userId}`)
        .then((response) => response.json())
        .then((data) => setNotifications(data))
        .catch((error) => console.error("Error fetching notifications:", error));
    }
  }, [isOpen, userId]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(4px)',
              zIndex: 50,
            }}
          />
          {/* Panel */}
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            style={{
              position: 'fixed',
              top: '80px',
              right: '24px',
              width: '380px',
              maxWidth: 'calc(100vw - 48px)',
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border-glass)',
              borderRadius: '20px',
              overflow: 'hidden',
              zIndex: 51,
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            }}
          >
            {/* Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px 24px',
              borderBottom: '1px solid var(--border-subtle)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: 'var(--accent-cyan)',
                  boxShadow: '0 0 8px rgba(var(--accent-primary-rgb), 0.4)',
                }} />
                <h2 style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                }}>Notifications</h2>
                {notifications.length > 0 && (
                  <span style={{
                    padding: '2px 10px',
                    borderRadius: '9999px',
                    background: 'rgba(var(--accent-primary-rgb), 0.1)',
                    border: '1px solid rgba(var(--accent-primary-rgb), 0.2)',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: 'var(--accent-cyan)',
                  }}>
                    {notifications.length}
                  </span>
                )}
              </div>
              <button
                onClick={onClose}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '10px',
                  border: '1px solid var(--border-glass)',
                  background: 'var(--bg-glass)',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = '#f87171';
                  e.currentTarget.style.color = '#f87171';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = 'var(--border-glass)';
                  e.currentTarget.style.color = 'var(--text-secondary)';
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Notification list */}
            <div style={{
              padding: '12px',
              maxHeight: '400px',
              overflowY: 'auto',
            }}>
              {notifications.length === 0 ? (
                <div style={{
                  padding: '40px 20px',
                  textAlign: 'center',
                  color: 'var(--text-muted)',
                  fontSize: '0.9rem',
                }}>
                  No notifications yet
                </div>
              ) : (
                notifications.map((notification, index) => (
                  <motion.div
                    key={notification.notification_id}
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    style={{
                      padding: '16px',
                      borderRadius: '14px',
                      marginBottom: '8px',
                      background: 'var(--bg-glass)',
                      border: '1px solid var(--border-subtle)',
                      transition: 'border-color 0.2s ease',
                    }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border-glass)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-subtle)'}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <div style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: notification.type === 'link' ? 'var(--accent-cyan)' : 'var(--accent-violet)',
                      }} />
                      <h3 style={{
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        color: 'var(--text-primary)',
                      }}>
                        {notification.type === "link" ? "Project Notification" : "General Notification"}
                      </h3>
                    </div>
                    <p style={{
                      fontSize: '0.8rem',
                      color: 'var(--text-secondary)',
                      lineHeight: 1.5,
                      marginBottom: '8px',
                      paddingLeft: '14px',
                    }}>{notification.message}</p>
                    {notification.type === "link" && (
                      <button
                        onClick={() => {
                          if (user.role === "professional") {
                            navigate(`/ProjectDetails/${encodeURIComponent(encryptId(notification.client_project_id))}`, { state: { notify: true } });
                          } else {
                            navigate(`/ProjectMap/${encodeURIComponent(encryptId(notification.client_project_id))}`);
                          }
                          onClose();
                        }}
                        style={{
                          marginLeft: '14px',
                          padding: '6px 14px',
                          borderRadius: '9999px',
                          border: '1px solid rgba(var(--accent-primary-rgb), 0.2)',
                          background: 'rgba(var(--accent-primary-rgb), 0.06)',
                          color: 'var(--accent-cyan)',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.background = 'rgba(var(--accent-primary-rgb), 0.12)';
                          e.currentTarget.style.borderColor = 'rgba(var(--accent-primary-rgb), 0.4)';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.background = 'rgba(var(--accent-primary-rgb), 0.06)';
                          e.currentTarget.style.borderColor = 'rgba(var(--accent-primary-rgb), 0.2)';
                        }}
                      >
                        View: {notification.project_title}
                      </button>
                    )}
                  </motion.div>
                ))
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

